import { Injectable, inject } from '@angular/core';
import { Auth } from '../auth/auth';
import { Permission, ROLES, RolId, hasPermission } from '../permissions/permissions';

export interface MenuItem {
  label: string;
  icon: string;
  route?: string;
  permission?: Permission;
  roles?: RolId[];
  submenu?: MenuItem[];
}

@Injectable({ providedIn: 'root' })
export class MenuService {
  private auth = inject(Auth);

  readonly items: MenuItem[] = [
    {
      label: 'Dashboard',
      icon: 'dashboard',
      route: '/home',
      roles: [
        ROLES.MONITORISTA,
        ROLES.SUPERVISOR,
        ROLES.TECNICO,
        ROLES.CLIENTE,
        ROLES.ADMINISTRADOR,
      ],
    },
    {
      label: 'Incidencias',
      icon: 'report',
      route: '/incidencias',
      roles: [
        ROLES.MONITORISTA,
        ROLES.SUPERVISOR,
        ROLES.TECNICO,
        ROLES.CLIENTE,
        ROLES.ADMINISTRADOR,
      ],
    },
    {
      label: 'Técnicos',
      icon: 'worker',
      route: '/tecnicos',
      roles: [
        ROLES.MONITORISTA,
        ROLES.SUPERVISOR,
        ROLES.ADMINISTRADOR,
      ],
    },
    {
      label: 'Teléfonos de Técnicos',
      icon: 'phone',
      route: '/telefonos',
      roles: [
        ROLES.MONITORISTA,
        ROLES.SUPERVISOR,
        ROLES.ADMINISTRADOR,
      ],
    },
    {
      label: 'Subir Evidencia',
      icon: 'camera',
      route: '/subir-evidencia',
      roles: [ROLES.TECNICO],
    },
    {
      label: 'Cargar Tarjetas',
      icon: 'credit-card',
      route: '/cargar-tarjetas',
      roles: [ROLES.TECNICO],
    },
    {
      label: 'Catálogos',
      icon: 'folder',
      roles: [ROLES.ADMINISTRADOR],
      submenu: [
        {
          label: 'Usuarios',
          icon: 'people',
          route: '/catalogos/usuarios',
          roles: [ROLES.ADMINISTRADOR],
        },
        {
          label: 'Teléfonos',
          icon: 'phone',
          route: '/catalogos/telefonos',
          roles: [ROLES.ADMINISTRADOR],
        },
        {
          label: 'Fallas y Soluciones',
          icon: 'warning',
          route: '/catalogos/fallas-soluciones',
          roles: [ROLES.ADMINISTRADOR],
        },
        {
          label: 'Equipos',
          icon: 'devices',
          route: '/catalogos/equipos',
          roles: [ROLES.ADMINISTRADOR],
        },
        {
          label: 'Tipos de Equipo',
          icon: 'device-hub',
          route: '/catalogos/tipos-equipo',
          roles: [ROLES.ADMINISTRADOR],
        },
      ],
    },
  ];

  get visibleItems(): MenuItem[] {
    const user = this.auth.user();
    if (!user) return [];

    // Si es login con PIN (técnico), mostrar Subir Evidencia y Cargar Tarjetas
    if (this.auth.isPinLogin) {
      return this.items.filter(item => item.route === '/subir-evidencia' || item.route === '/cargar-tarjetas');
    }

    return this.items.filter((item) => {
      if (item.roles) {
        return item.roles.includes(user.idRol as RolId);
      }
      if (item.permission) {
        return hasPermission(item.permission, user.idRol as RolId);
      }
      return true;
    });
  }

  getIcon(name: string): string {
    const icons: Record<string, string> = {
      dashboard: 'bi bi-speedometer2',
      report: 'bi bi-clipboard2',
      people: 'bi bi-people',
      analytics: 'bi bi-bar-chart',
      settings: 'bi bi-gear',
      camera: 'bi bi-camera',
      worker: 'bi bi-person-badge',
      phone: 'bi bi-telephone',
      'credit-card': 'bi bi-credit-card',
      folder: 'bi bi-folder',
      warning: 'bi bi-exclamation-triangle',
      devices: 'bi bi-pc-display',
      'device-hub': 'bi bi-hdd-stack',
    };
    return icons[name] || 'bi bi-circle';
  }
}
